import React, { useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './context';
import { SpaRecoveryPanel } from './components/SpaRecoveryPanel';
import { AppMessages } from './components/AppMessages';
import { finishStartup } from './theme/startup';
import AppLayout from './web/components/layout/AppLayout';
import { AppContextProvider, type ApiUrls } from './context';
import type { CustomUser, Team as ApiTeam } from 'api-client/models';
import { CustomUserFromJSON, TeamFromJSON } from 'api-client/models';

// Import application routes
import TeamsPage from './teams/components/TeamsPage';
import { VenuesPage } from './venues/components';
import { StagesPage } from './stages/components';
import { PerformersPage } from './performers/components';
import { ShowsPage } from './shows/components';
import { BookingsPage } from './bookings';
import {
  LoginForm,
  SignupForm,
  LogoutForm,
  PasswordResetForm,
  PasswordResetConfirmForm,
  EmailVerificationForm,
  ProfileForm,
  SocialLoginForm,
  SocialSignupForm,
  SocialConnectionsForm,
  SocialConnectionsPage,
  SocialAuthenticationError,
  EmailVerificationPage,
  TwoFactorAuthPage,
  ChangePasswordPage,
} from './users/components/auth';
import UserDashboard from './dashboard/components/user/UserDashboard';
import ChatPage from './chat/components/ChatPage';
import GroupChatPage from './group-chat/components/GroupChatPage';
import SubscriptionsPage from './subscriptions/components/SubscriptionsPage';
import TermsPage from './web/components/TermsPage';
import { HomeRedirect } from './web/components/landing';
import { ErrorBoundary, Error404 } from './utils/components/errors';
import {
  AdminDashboard,
  UserManagement,
  UserImpersonation,
  ReturnToAdmin,
  AdminBridge,
  SupportTools,
  ProjectDashboard,
} from './dashboard/components/admin';
import { SuperuserOnlyRoute } from './utils/components/security';

// Extend Window interface for Django context
declare global {
  interface Window {
    SERVER_URL_BASE?: string;
    DEBUG?: string | boolean;
    AI_CHAT_ENABLED?: boolean;
    urlBase?: string;
  }
}

/**
 * Single Entry Point Router for React Apps
 *
 * This router handles client-side routing for all React sections,
 * with MUI-based layout components replacing the Django navbar.
 */
function MainApplication(): React.ReactElement {
  // Get context from Django-injected JSON and convert snake_case to camelCase
  const apiUrls: ApiUrls = JSON.parse(document.getElementById('api-urls')?.textContent || '{}');

  // Parse user data and convert from snake_case to camelCase using API client converter
  const userDataRaw = JSON.parse(document.getElementById('user-details')?.textContent || 'null');
  const user: CustomUser | null = userDataRaw ? CustomUserFromJSON(userDataRaw) : null;

  // Parse team data and convert from snake_case to camelCase using API client converter
  const teamJson = document.getElementById('team-details')?.textContent;
  const teamDataRaw = teamJson ? JSON.parse(teamJson) : null;
  const team: ApiTeam | null = teamDataRaw ? TeamFromJSON(teamDataRaw) : null;

  return (
    <AppContextProvider user={user} team={team} apiUrls={apiUrls}>
      <AppLayout user={user} team={team} title="Solid Slate">
        <AppMessages />
        <Routes>
          {/* Teams Management Routes */}
          <Route path="/teams/*" element={<TeamsPage />} />

          {/* Venues & Stages */}
          <Route path="/venues/*" element={<VenuesPage />} />

          {/* Stages Management */}
          <Route path="/stages/*" element={<StagesPage />} />

          {/* Performers */}
          <Route path="/performers/*" element={<PerformersPage />} />

          {/* Shows & Calendar */}
          <Route path="/shows/*" element={<ShowsPage />} />

          {/* Bookings & Holds */}
          <Route path="/bookings/*" element={<BookingsPage />} />

          {/* AI Chat Routes */}
          {window.AI_CHAT_ENABLED !== false && <Route path="/chat/*" element={<ChatPage />} />}

          {/* Group Chat Routes */}
          <Route path="/group-chat/*" element={<GroupChatPage />} />

          {/* Subscriptions & Billing Routes */}
          <Route path="/a/:teamSlug/subscription/*" element={<SubscriptionsPage />} />

          {/* Superuser Routes - /superuser/* avoids colliding with Django admin at /admin/ */}
          <Route
            path="/superuser/dashboard/"
            element={
              <SuperuserOnlyRoute>
                <AdminDashboard />
              </SuperuserOnlyRoute>
            }
          />
          <Route
            path="/superuser/users/"
            element={
              <SuperuserOnlyRoute>
                <UserManagement />
              </SuperuserOnlyRoute>
            }
          />
          <Route path="/support/" element={<UserImpersonation />} />
          <Route path="/superuser/impersonate/" element={<UserImpersonation />} />
          <Route path="/superuser/return/" element={<ReturnToAdmin />} />
          <Route
            path="/superuser/bridge/"
            element={
              <SuperuserOnlyRoute>
                <AdminBridge />
              </SuperuserOnlyRoute>
            }
          />
          <Route
            path="/superuser/support/"
            element={
              <SuperuserOnlyRoute>
                <SupportTools />
              </SuperuserOnlyRoute>
            }
          />

          {/* Landing & legal pages */}
          <Route index element={<HomeRedirect />} />
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Team Dashboard Route - /a/{teamSlug}/ should show dashboard */}
          <Route path="/a/:teamSlug/" element={<UserDashboard />} />

          {/* Home route now points to LandingPage; team users can navigate to their dashboard via nav */}

          {/* Authentication Routes */}
          <Route path="/auth/login" element={<LoginForm />} />
          <Route path="/auth/signup" element={<SignupForm />} />
          <Route path="/auth/logout" element={<LogoutForm />} />
          <Route path="/auth/password-reset" element={<PasswordResetForm />} />
          <Route path="/auth/password-reset-confirm" element={<PasswordResetConfirmForm />} />
          <Route path="/auth/email-verify" element={<EmailVerificationForm />} />
          <Route path="/auth/profile" element={<ProfileForm />} />

          {/* Profile management pages */}
          <Route path="/auth/email" element={<EmailVerificationPage />} />
          <Route path="/auth/2fa" element={<TwoFactorAuthPage />} />
          <Route path="/auth/change-password" element={<ChangePasswordPage />} />

          {/* Profile alias for easier access */}
          <Route path="/profile" element={<ProfileForm />} />
          <Route path="/email-verify" element={<EmailVerificationPage />} />
          <Route path="/2fa" element={<TwoFactorAuthPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />

          {/* Social Authentication Routes */}
          <Route path="/auth/social/login" element={<SocialLoginForm />} />
          <Route path="/auth/social/login/:providerId" element={<SocialLoginForm />} />
          <Route path="/auth/social/connect/:providerId" element={<SocialLoginForm />} />
          <Route path="/auth/social/signup" element={<SocialSignupForm />} />
          <Route path="/auth/social/connections" element={<SocialConnectionsForm />} />
          <Route path="/auth/social/manage" element={<SocialConnectionsPage />} />
          <Route path="/auth/social/error" element={<SocialAuthenticationError />} />

          {/* Social connections management alias */}
          <Route path="/social-connections" element={<SocialConnectionsPage />} />
          <Route path="/social-connections/" element={<SocialConnectionsPage />} />

          {/* Dashboard Routes */}
          <Route path="/dashboard" element={<UserDashboard />} />

          {/* Project Dashboard Route - Admin Analytics */}
          <Route
            path="/project-dashboard"
            element={
              <SuperuserOnlyRoute>
                <ProjectDashboard />
              </SuperuserOnlyRoute>
            }
          />

          <Route path="*" element={<Error404 />} />
        </Routes>
      </AppLayout>
    </AppContextProvider>
  );
}

export default function App(): React.ReactElement {
  useLayoutEffect(finishStartup, []);
  // Always use root as basePath since SPA is now at root
  const basePath: string = '/';

  // Debug logging removed for lint compliance

  return (
    <BrowserRouter basename={basePath}>
      <ThemeProvider>
        <ErrorBoundary fallback={<SpaRecoveryPanel />}>
          <MainApplication />
        </ErrorBoundary>
      </ThemeProvider>
    </BrowserRouter>
  );
}
